import { useRef, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBookmark,
  faPlus,
  faXmark,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "../../components/ui/dialog/Dialog";
import type { ResourceList } from "../../services/api/playground";

interface AddResourceListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resourceListName: string;
  setResourceListName: (name: string) => void;
  useCurrentResources: boolean;
  setUseCurrentResources: (useCurrent: boolean) => void;
  hasCurrentResources: boolean;
  onAddResourceList: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function AddResourceListDialog({
  isOpen,
  onClose,
  resourceListName,
  setResourceListName,
  useCurrentResources,
  setUseCurrentResources,
  hasCurrentResources,
  onAddResourceList,
  inputRef,
}: AddResourceListDialogProps) {
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Add Resource List</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          <div>
            <label
              htmlFor="resource-list-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Resource List Name
            </label>
            <input
              type="text"
              id="resource-list-name"
              ref={inputRef}
              value={resourceListName}
              onChange={(e) => setResourceListName(e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-700 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
              placeholder="Enter resource list name"
              onKeyDown={(e) => {
                if (e.key === "Enter" && resourceListName.trim()) {
                  onAddResourceList();
                }
              }}
            />
          </div>
          {hasCurrentResources && (
            <div className="space-y-3">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Resource List Type
              </div>
              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={useCurrentResources}
                    onChange={() => setUseCurrentResources(true)}
                    className="text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Start with current resources
                  </span>
                </label>
                <label className="flex items-center space-x-2">
                  <input
                    type="radio"
                    checked={!useCurrentResources}
                    onChange={() => setUseCurrentResources(false)}
                    className="text-indigo-500 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">
                    Start with an empty list
                  </span>
                </label>
              </div>
            </div>
          )}
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {hasCurrentResources
              ? "Create a new resource list with your current selection or start fresh."
              : "Create a new resource list to save your favorite resources."}
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onAddResourceList}
          disabled={!resourceListName.trim()}
          className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add Resource List
        </button>
      </DialogActions>
    </Dialog>
  );
}

interface SaveResourceListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resourceListName: string;
  setResourceListName: (name: string) => void;
  onSaveResourceList: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

export function SaveResourceListDialog({
  isOpen,
  onClose,
  resourceListName,
  setResourceListName,
  onSaveResourceList,
  inputRef,
}: SaveResourceListDialogProps) {
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Save Current Selection</DialogTitle>
      <DialogBody>
        <div className="space-y-6 py-4">
          <div>
            <label
              htmlFor="save-resource-list-name"
              className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              Resource List Name
            </label>
            <input
              id="save-resource-list-name"
              type="text"
              ref={inputRef}
              value={resourceListName}
              onChange={(e) => setResourceListName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white"
              placeholder="My Resource List"
            />
          </div>

          <div className="text-sm text-gray-500 dark:text-gray-400">
            Save the current selected resources as a new resource list.
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onSaveResourceList}
          disabled={!resourceListName.trim()}
          className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save Resource List
        </button>
      </DialogActions>
    </Dialog>
  );
}

interface SelectResourceListDialogProps {
  isOpen: boolean;
  onClose: () => void;
  resourceLists: ResourceList[];
  onSelectResourceList: (resourceListId: string) => void;
  onCreateNewResourceList: () => void;
  isLoading?: boolean;
}

export function SelectResourceListDialog({
  isOpen,
  onClose,
  resourceLists,
  onSelectResourceList,
  onCreateNewResourceList,
  isLoading = false,
}: SelectResourceListDialogProps) {
  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle>Select Resource List</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {resourceLists.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No resource lists found.
            </div>
          ) : (
            <div className="space-y-2">
              {resourceLists.map((resourceList) => (
                <button
                  key={resourceList.id}
                  onClick={() => onSelectResourceList(resourceList.id!)}
                  className="w-full p-3 text-left rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {resourceList.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {resourceList.items.length} resources
                      </div>
                    </div>
                    <FontAwesomeIcon
                      icon={faBookmark}
                      className="h-4 w-4 text-indigo-500"
                    />
                  </div>
                </button>
              ))}
            </div>
          )}

          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onCreateNewResourceList}
              className="w-full p-3 text-left rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-700 text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <div className="flex items-center">
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4 mr-2" />
                <span>Create New Resource List</span>
              </div>
            </button>
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md"
        >
          Cancel
        </button>
      </DialogActions>
    </Dialog>
  );
}
